import {json} from "d3-fetch";
import {create, select} from 'd3-selection';
import {geoPath} from "d3-geo";
import {descending, max} from "d3-array";
import {scaleSqrt} from "d3-scale";
import {format as d3Format} from "d3-format";
import {transition} from "d3-transition"; // ignore unused import error
import * as usMap from "@d3ts/us-map";
import * as topojson from "topojson-client";
import topologyAlbers from "@d3ts/us-atlas/counties-albers-10m.json";
import topologyOutlines from "@d3ts/us-atlas/outlines-albers-10m.json";
import noUiSlider from 'nouislider';
import 'nouislider/dist/nouislider.css';
import moment from "moment";

let selectedState;
let chart;
let mobileTooltipCount = 0;
const renderNationalTotalFacilitiesTrend = async () => {
    if ($(".national-total-active-facilities-widget").length <= 0  ) {
        return;
    }

    let config = {
        layer: 'states',
        scope: 'all',
        zoomable: false,
        outlines: true,
        scaleStroke: true,
        sync: false,
        labels: window.mapLabels ? window.mapLabels : false,
    };

    const scopeMap = ({
        all: usMap.scopes.all(),
        states: usMap.scopes.states(),
        lower48: usMap.scopes.lower48(),
    });

    const featureMap = ({
        nation: usMap.nation,
        states: usMap.states,
        counties: usMap.counties,
    });

    const scope = scopeMap[config.scope],
        map = config.zoomable
            // If we want a zoom-able map we simply wrap the map factory output in the zoom-able mixin
            ? usMap.zoomable(usMap.usMap(scope))
            : usMap.usMap(scope),
        // Get the right pre-made feature, loading the pre-projected topology and scope, then tweak some settings
        prelimFeature = featureMap[config.layer](topologyAlbers, scope)
            .width(0.5)
            .strokeColor(['#777777']) // #777777
            .fillColor(['#f5f5f5']), // #f5f5f5
        // Scale the feature's stroke if that setting is active
        feature = config.scaleStroke ? usMap.scaleWidth(prelimFeature, map) : prelimFeature,
        svg = create('svg'),
        // Finally we call the map with the svg selection, returning the group
        // No need to bind data in pre-made features
        g = map(svg).call(feature);

    // If the option to show inset outlines is checked, then add to the map
    // Inset outlines are a special feature that comes as a separate pre-projected TopoJSON fom the us-atlas package
    if (config.outlines)
        usMap.outlineMesh(topologyOutlines, scope)(g);

    // get the state data
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const projection = usMap.usMap().projection();

    const path = geoPath()
        .projection(projection)
        .pointRadius(2.5);

    let minDate;
    await json(`/wp-json/data/national-active-facilities-trend${config.labels ? '?labels=true' : ''}`)
        .then(async (response) => {
            let {totalCount, data, dates, states} = response;
            let labels;
            if (config.labels) {
                ({labels} = response);
            }
            // state-list-control
            const stateListControl = select(".national-total-active-facilities-widget #state-list-control");
            stateListControl
                .selectAll("option.state")
                .data(states)
                .enter()
                .append("option")
                .attr("value", d => d.state)
                .text(d => d.name)
                .classed("state");
            minDate = dates.min;
            chart = await processData(config, projection, svg, topojson, totalCount, data, dates, labels);
            select(".national-total-active-facilities-widget #trend-map")
                .node()
                .appendChild(chart);

            select("#btn-play")
                .attr("data-min", moment(dates.min, "YYYY-MM-DD Z").valueOf())
                .attr("data-max", moment(dates.max, "YYYY-MM-DD Z").valueOf());
        })
        .catch(error => console.error('error', error));

    // if (chart) {
    //     chart.update(moment(minDate, "YYYY-MM-DD Z").valueOf(), selectedState);
    // }
};

const processData = async (config, projection, svg, topojson, totalCount, data, dates, labels) => {
    let stateMap = new Map(
        topojson.feature(topologyAlbers, topologyAlbers.objects.states)
            .features.map(d => [d.id, d]));

    data = data.map((d) => ({
        ...d,
        st: stateMap.get(d.fips)
    }))
        .sort((a, b) => descending(a.count, b.count));

    // Construct the radius scale.
    const radius = scaleSqrt([0, max(data, d => d.count)], [0, 40]);

    // Construct a path generator.
    const path = geoPath();

    // Create the legend.
    const legend = svg.append("g")
        .attr("fill", "#777")
        .attr("transform", "translate(915,480)")
        .attr("text-anchor", "middle")
        .style("font", "10px sans-serif")
        .selectAll()
        .data(radius.ticks(5).slice(1))
        .join("g");

    legend.append("circle")
        .attr("fill", "none")
        .attr("stroke", "#ccc") //
        .attr("cy", d => -radius(d))
        .attr("r", radius);

    legend.append("text")
        .attr("y", d => -2 * radius(d))
        .attr("dy", "1.2em")
        .text(radius.tickFormat(4, "s"));

    // Add a circle for each county, with a title (tooltip).
    const format = d3Format(",.0f");

    const dot = svg.append("g")
        .attr("fill", "#c894b4") // Circle colors // #bd43a0 // "#c894b4"
        .attr("fill-opacity", 0.5)
        .attr("stroke", "#FFFFFF")
        .attr("stroke-width", 0.5)
        .selectAll("circle")
        .data(data);

    const circle =
        dot
            .join("circle")
            .attr("transform", d => `translate(${path.centroid(d.st)})`)
            .attr("class", "data");

    // create a tooltip
    let tooltip = select("#trend-map .tooltip");
    let tooltipText = tooltip.select(".tooltip-text");
    select("#trend-map .tooltip .tooltip-content .tooltip-close")
        .on("click", function () {
            tooltip.classed("hidden", true);
        });

    let mobileTooltip = select(".legend .tooltip");

    circle
        .on("click", function (d) {
            if (d.target) {
                if (d.target.__data__) {
                    if (tooltip) {
                        let {date, count, st} = d.target.__data__;
                        let {name} = st.properties;
                        name = name.toUpperCase();
                        date = moment(date).format("MMMM YYYY").toUpperCase();
                        tooltipText
                            .html(`<div><h4 class="title">${date}</h4> <h3 class="state">${name}</h3></div>
                                   <div><h4 class="title">${window.mapActiveFacilitiesTitle}</h4> <h3 class="count">${format(count)}</h3></div>`);
                        tooltip.classed("hidden", false);
                        tooltip
                            .style("top", (d.offsetY  - 75) + "px")
                            .style("left", (d.offsetX - 215) + "px");
                    }
                }
            }
        })

    if (config.labels && labels) {
        labels = labels.map((d) => ({
            ...d,
            st: stateMap.get(d.fips)
        }))

        // Add a label for each state.
        const label = svg.append("g")
            .attr("font-family", "sans-serif")
            .attr("font-size", 7)
            .attr("fill", "black")
            .attr("text-anchor", "middle")
            .selectAll()
            .data(labels)
            .join("text")
            .attr("class", "labels")
            .attr("dy", d => {
                if (d.st.properties.code === "FL") {
                    return "-3";
                }
                return "1";
            })
            .attr("dx", d => {
                if (d.st.properties.code === "FL") {
                    return "10";
                }
                return "0";
            })
            .attr("transform", d => `translate(${path.centroid(d.st)})`)
            .text(d => d.state);
    }

    let totalCountText = select("#total-count");
    totalCountText.text(format(totalCount));

    let dateSlider = document.getElementById('slider-date');

    const startDate = dates.min, // "2015-02-01",
        endDate = dates.max; // "2024-01-01"

    noUiSlider.create(dateSlider, {
        // Create two timestamps to define a range.
        range: {
            min: moment(startDate, "YYYY-MM-DD Z").valueOf(),
            max: moment(endDate, "YYYY-MM-DD Z").valueOf(),
        },

        // steps on one day
        step: 24 * 60 * 60 * 1000,

        start: moment(startDate, "YYYY-MM-DD Z").valueOf(),

        // No decimals
        format: {
            to: (value) => value.toFixed(0),
            from: (value) => value.replace(',-', '')
        },

        connect: "lower"
    });

    let dateValues = [
        document.getElementById('event-start'),
        // document.getElementById('event-end')
    ];

    let formatter = new Intl.DateTimeFormat('en-US', {
        dateStyle: 'full'
    });

    dateSlider.noUiSlider.on('update', function (values, handle) {
        if (dateValues[handle]) {
            dateValues[handle].innerHTML = moment(moment(+values[handle]).toDate()).format("MMMM YYYY").toUpperCase();

            if (chart && chart.update) {
                chart.update(+values[0], selectedState);
            }
        }
    });

    // On Click event for the play button to auto play the date slider
    const playButton = document.getElementById('btn-play');
    let isPlaying = false;
    let interval;
    playButton.addEventListener('click', function (e) {
        const min = +playButton.getAttribute("data-min");
        const max = +playButton.getAttribute("data-max");

        // if the slider is at the end, reset it to the beginning
        if (+dateSlider.noUiSlider.get() >= max) {
            dateSlider.noUiSlider.set(min);
        }

        if (isPlaying) {
            clearInterval(interval);
            isPlaying = false;
            playButton.textContent = 'Play';
            // remove ".pause" class
            playButton.classList.remove("pause");
        } else {
            isPlaying = true;
            playButton.textContent = 'Pause';
            // add ".pause" class
            playButton.classList.add("pause");
            interval = setInterval(() => {
                const val = dateSlider.noUiSlider.get();
                const next = moment(+val).add(1, 'month').valueOf();
                if (next > +max) {
                    dateSlider.noUiSlider.set(next);
                    clearInterval(interval);
                    isPlaying = false;
                    // remove ".pause" class
                    playButton.classList.remove("pause");
                    playButton.textContent = 'Play';
                } else {
                    dateSlider.noUiSlider.set(next);
                }
            }, 150);
        }
    });
    // btn-play

    select("#state-list-control")
        .on("change", function () {
            selectedState = this.value === "null" ? null : this.value;
            if (chart && chart.update) {
                if (selectedState) {
                    mobileTooltipCount = 0;

                    // if the slider value is greater than the min date
                    // send the parameter to calculate the active facilities from the min date up to the slider value
                    let calculateActiveFacilities = false;
                    if (+dateSlider.noUiSlider.get() >= moment(dates.min, "YYYY-MM-DD Z").valueOf()) {
                        calculateActiveFacilities = true;
                    }
                    chart.update(+dateSlider.noUiSlider.get(), selectedState, calculateActiveFacilities);
                } else {
                    chart.resetStateSelector();
                    mobileTooltip.classed("hidden", true);
                }
                tooltip.classed("hidden", true);
            }
        });

    const mobileTooltipHandler = (selectedState, count) => {
        if (selectedState) {
            let date = $('.slider-date-val').text();
            let state = $('#state-list-control')[0].selectedOptions[0].text;
            state = state.toUpperCase();
            $(".legend .tooltip .tooltip-content .tooltip-text .title.date")[0].innerText = date;
            $(".legend .tooltip .tooltip-content .tooltip-text .state")[0].innerText = state;
            $(".legend .tooltip .tooltip-content .tooltip-text .count")[0].innerText = format(count ?? 0);
            mobileTooltip.classed("hidden", false);
        } else {
            mobileTooltip.classed("hidden", true);
        }
    }

    /**
     * Pattern
     */
        // Attach the pattern to the svg
    const defs = svg.append("defs");
    // <pattern id="pattern_entity_covered_coverage" patternUnits="userSpaceOnUse" viewBox="0,0,6000,4000" width="100%" height="100%">
    const pattern = defs.append("pattern")
        .attr("id", "pattern_entity_covered_coverage")
        .attr("patternUnits", "userSpaceOnUse")
        .attr("viewBox", "0,0,6000,4000")
        .attr("width", "100%")
        .attr("height", "100%");
    // <polygon class="cls-2" points="5900.31 4000 6000 3900.31 6000 3758.89 5758.89 4000 5900.31 4000"/>
    pattern.append("polygon")
        .attr("class", "cls-2")
        .attr("points", "5900.31 4000 6000 3900.31 6000 3758.89 5758.89 4000 5900.31 4000");
    // <polyline class="cls-4" points="5476.04 4000 5617.46 4000 6000 3617.46 6000 3476.04"/>
    pattern.append("polyline")
        .attr("class", "cls-4")
        .attr("points", "5476.04 4000 5617.46 4000 6000 3617.46 6000 3476.04");
    // <polygon class="cls-2" points="5334.62 4000 6000 3334.62 6000 3193.2 5193.2 4000 5334.62 4000"/>
    pattern.append("polygon")
        .attr("class", "cls-2")
        .attr("points", "5334.62 4000 6000 3334.62 6000 3193.2 5193.2 4000 5334.62 4000");
    // <polygon class="cls-4" points="5051.78 4000 6000 3051.78 6000 2910.36 4910.36 4000 5051.78 4000"/>
    pattern.append("polygon")
        .attr("class", "cls-4")
        .attr("points", "5051.78 4000 6000 3051.78 6000 2910.36 4910.36 4000 5051.78 4000");
    // <polygon class="cls-2" points="4768.94 4000 6000 2768.94 6000 2627.51 4627.51 4000 4768.94 4000"/>
    pattern.append("polygon")
        .attr("class", "cls-2")
        .attr("points", "4768.94 4000 6000 2768.94 6000 2627.51 4627.51 4000 4768.94 4000");
    // <polygon class="cls-4" points="4486.09 4000 6000 2486.09 6000 2344.67 4344.67 4000 4486.09 4000"/>
    pattern.append("polygon")
        .attr("class", "cls-4")
        .attr("points", "4486.09 4000 6000 2486.09 6000 2344.67 4344.67 4000 4486.09 4000");
    // <polygon class="cls-2" points="4203.25 4000 6000 2203.25 6000 2061.83 4061.83 4000 4203.25 4000"/>
    pattern.append("polygon")
        .attr("class", "cls-2")
        .attr("points", "4203.25 4000 6000 2203.25 6000 2061.83 4061.83 4000 4203.25 4000");
    // <polygon class="cls-4" points="3920.41 4000 6000 1920.41 6000 1778.99 3778.99 4000 3920.41 4000"/>
    pattern.append("polygon")
        .attr("class", "cls-4")
        .attr("points", "3920.41 4000 6000 1920.41 6000 1778.99 3778.99 4000 3920.41 4000");
    // <polygon class="cls-2" points="3637.57 4000 6000 1637.57 6000 1496.14 3496.14 4000 3637.57 4000"/>
    pattern.append("polygon")
        .attr("class", "cls-2")
        .attr("points", "3637.57 4000 6000 1637.57 6000 1496.14 3496.14 4000 3637.57 4000");
    // <polygon class="cls-4" points="3354.72 4000 6000 1354.72 6000 1213.3 3213.3 4000 3354.72 4000"/>
    pattern.append("polygon")
        .attr("class", "cls-4")
        .attr("points", "3354.72 4000 6000 1354.72 6000 1213.3 3213.3 4000 3354.72 4000");
    // <polygon class="cls-2" points="3071.88 4000 6000 1071.88 6000 930.46 2930.46 4000 3071.88 4000"/>
    pattern.append("polygon")
        .attr("class", "cls-2")
        .attr("points", "3071.88 4000 6000 1071.88 6000 930.46 2930.46 4000 3071.88 4000");
    // <polygon class="cls-4" points="2789.04 4000 6000 789.04 6000 647.62 2647.62 4000 2789.04 4000"/>
    pattern.append("polygon")
        .attr("class", "cls-4")
        .attr("points", "2789.04 4000 6000 789.04 6000 647.62 2647.62 4000 2789.04 4000");
    // <polygon class="cls-2" points="2506.19 4000 6000 506.19 6000 364.77 2364.77 4000 2506.19 4000"/>
    pattern.append("polygon")
        .attr("class", "cls-2")
        .attr("points", "2506.19 4000 6000 506.19 6000 364.77 2364.77 4000 2506.19 4000");
    // <polygon class="cls-4" points="2223.35 4000 6000 223.35 6000 81.93 2081.93 4000 2223.35 4000"/>
    pattern.append("polygon")
        .attr("class", "cls-4")
        .attr("points", "2223.35 4000 6000 223.35 6000 81.93 2081.93 4000 2223.35 4000");
    // <polygon class="cls-2" points="5799.09 0 1799.09 4000 1940.51 4000 5940.51 0 5799.09 0"/>
    pattern.append("polygon")
        .attr("class", "cls-2")
        .attr("points", "5799.09 0 1799.09 4000 1940.51 4000 5940.51 0 5799.09 0");
    // <polygon class="cls-4" points="5516.25 0 1516.24 4000 1657.67 4000 5657.67 0 5516.25 0"/>
    pattern.append("polygon")
        .attr("class", "cls-4")
        .attr("points", "5516.25 0 1516.24 4000 1657.67 4000 5657.67 0 5516.25 0");
    // <polygon class="cls-2" points="5233.4 0 1233.4 4000 1374.82 4000 5374.82 0 5233.4 0"/>
    pattern.append("polygon")
        .attr("class", "cls-2")
        .attr("points", "5233.4 0 1233.4 4000 1374.82 4000 5374.82 0 5233.4 0");
    // <polygon class="cls-4" points="4950.56 0 950.56 4000 1091.98 4000 5091.98 0 4950.56 0"/>
    pattern.append("polygon")
        .attr("class", "cls-4")
        .attr("points", "4950.56 0 950.56 4000 1091.98 4000 5091.98 0 4950.56 0");
    // <polygon class="cls-2" points="4667.72 0 667.72 4000 809.14 4000 4809.14 0 4667.72 0"/>
    pattern.append("polygon")
        .attr("class", "cls-2")
        .attr("points", "4667.72 0 667.72 4000 809.14 4000 4809.14 0 4667.72 0");
    // <polygon class="cls-4" points="4384.87 0 384.87 4000 526.3 4000 4526.3 0 4384.87 0"/>
    pattern.append("polygon")
        .attr("class", "cls-4")
        .attr("points", "4384.87 0 384.87 4000 526.3 4000 4526.3 0 4384.87 0");
    // <polygon class="cls-2" points="4102.03 0 102.03 4000 243.45 4000 4243.45 0 4102.03 0"/>
    pattern.append("polygon")
        .attr("class", "cls-2")
        .attr("points", "4102.03 0 102.03 4000 243.45 4000 4243.45 0 4102.03 0");
    // <polygon class="cls-4" points="3819.19 0 0 3819.19 0 3960.61 3960.61 0 3819.19 0"/>
    pattern.append("polygon")
        .attr("class", "cls-4")
        .attr("points", "3819.19 0 0 3819.19 0 3960.61 3960.61 0 3819.19 0");
    // <polygon class="cls-2" points="3536.35 0 0 3536.35 0 3677.77 3677.77 0 3536.35 0"/>
    pattern.append("polygon")
        .attr("class", "cls-2")
        .attr("points", "3536.35 0 0 3536.35 0 3677.77 3677.77 0 3536.35 0");
    // <polygon class="cls-4" points="3253.5 0 0 3253.5 0 3394.92 3394.92 0 3253.5 0"/>
    pattern.append("polygon")
        .attr("class", "cls-4")
        .attr("points", "3253.5 0 0 3253.5 0 3394.92 3394.92 0 3253.5 0");
    // <polygon class="cls-2" points="2970.66 0 0 2970.66 0 3112.08 3112.08 0 2970.66 0"/>
    pattern.append("polygon")
        .attr("class", "cls-2")
        .attr("points", "2970.66 0 0 2970.66 0 3112.08 3112.08 0 2970.66 0");
    // <polygon class="cls-4" points="2687.82 0 0 2687.82 0 2829.24 2829.24 0 2687.82 0"/>
    pattern.append("polygon")
        .attr("class", "cls-4")
        .attr("points", "2687.82 0 0 2687.82 0 2829.24 2829.24 0 2687.82 0");
    // <polygon class="cls-2" points="2404.98 0 0 2404.98 0 2546.4 2546.4 0 2404.98 0"/>
    pattern.append("polygon")
        .attr("class", "cls-2")
        .attr("points", "2404.98 0 0 2404.98 0 2546.4 2546.4 0 2404.98 0");
    // <polygon class="cls-4" points="2122.13 0 0 2122.13 0 2263.55 2263.55 0 2122.13 0"/>
    pattern.append("polygon")
        .attr("class", "cls-4")
        .attr("points", "2122.13 0 0 2122.13 0 2263.55 2263.55 0 2122.13 0");
    // <polygon class="cls-2" points="1839.29 0 0 1839.29 0 1980.71 1980.71 0 1839.29 0"/>
    pattern.append("polygon")
        .attr("class", "cls-2")
        .attr("points", "1839.29 0 0 1839.29 0 1980.71 1980.71 0 1839.29 0");
    // <polygon class="cls-4" points="1556.45 0 0 1556.45 0 1697.87 1697.87 0 1556.45 0"/>
    pattern.append("polygon")
        .attr("class", "cls-4")
        .attr("points", "1556.45 0 0 1556.45 0 1697.87 1697.87 0 1556.45 0");
    // <polygon class="cls-2" points="1273.6 0 0 1273.6 0 1415.03 1415.03 0 1273.6 0"/>
    pattern.append("polygon")
        .attr("class", "cls-2")
        .attr("points", "1273.6 0 0 1273.6 0 1415.03 1415.03 0 1273.6 0");
    // <polygon class="cls-4" points="990.76 0 0 990.76 0 1132.18 1132.18 0 990.76 0"/>
    pattern.append("polygon")
        .attr("class", "cls-4")
        .attr("points", "990.76 0 0 990.76 0 1132.18 1132.18 0 990.76 0");
    // <polygon class="cls-2" points="707.92 0 0 707.92 0 849.34 849.34 0 707.92 0"/>
    pattern.append("polygon")
        .attr("class", "cls-2")
    // <polygon class="cls-4" points="425.08 0 0 425.08 0 566.5 566.5 0 425.08 0"/>
    pattern.append("polygon")
        .attr("class", "cls-4")
        .attr("points", "425.08 0 0 425.08 0 566.5 566.5 0 425.08 0");
    // <polygon class="cls-2" points="142.23 0 0 142.23 0 283.65 283.65 0 142.23 0"/>
    pattern.append("polygon")
        .attr("class", "cls-2")
        .attr("points", "142.23 0 0 142.23 0 283.65 283.65 0 142.23 0");
    // <polygon class="cls-1" points="0 0 0 .81 .81 0 0 0"/>
    pattern.append("polygon")
        .attr("class", "cls-1")
        .attr("points", "0 0 0 .81 .81 0 0 0");

    let mapStates = svg.selectAll("path.state");
    if (window.mapEntityCoveredCcoverage) {
        let entityCoveredStates = window.mapEntityCoveredCcoverage.split(",");
        entityCoveredStates.forEach((state) => {
            if (state) {
                state = state.trim().toUpperCase();
            }
            // Apply the pattern to the state
            mapStates.filter(d => d.properties.code === state)
                .attr("fill", "url(#pattern_entity_covered_coverage)");
        });
    }

    if (window.mapStatewideCoverage) {
        let statewideCoverageStates = window.mapStatewideCoverage.split(",");
        statewideCoverageStates.forEach((state) => {
            if (state) {
                state = state.trim().toUpperCase();
            }
            // Apply the pattern to the state
            mapStates.filter(d => d.properties.code === state)
                .attr("fill", "#909090");
        });
    }

    let previousDate = -Infinity;
    return Object.assign(
        svg.node(), {
            update(currentDate, selectedState, calculateActiveFacilities = false) {
                // Calculate the national total active facilities count up to the current date
                let nationalTotalActiveFacilities = data
                    .filter(d => moment(d.date, "MMMM YYYY").valueOf() <= currentDate)
                    .reduce((acc, d) => {
                        return acc + d.count;
                    }, 0);
                totalCountText.text(format(nationalTotalActiveFacilities));

                if (selectedState) {
                    // Clear all the circle points from the map
                    circle
                        .filter(d => d.state !== selectedState)
                        .transition().duration(150)
                        .attr("style", "opacity: 0");

                    // Show the circle points for the selected state
                    circle
                        .filter(d => d.state === selectedState)
                        .transition().duration(150)
                        .attr("style", "opacity: 0.75");

                    if (calculateActiveFacilities) {
                        // Calculate the active facilities from the min date up to the current date
                        mobileTooltipCount = data
                            .filter(d => d.state === selectedState)
                            .filter(d => moment(d.date, "MMMM YYYY").valueOf() <= currentDate)
                            .reduce((acc, d) => {
                                return acc + d.count;
                            }, 0);
                    }

                    circle // enter
                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                        .filter(d => d.state === selectedState)
                        .filter(d => {
                            // const valid = d.date > previousDate && d.date <= currentDate;
                            const valid = moment(d.date, "MMMM YYYY").valueOf() > previousDate &&
                                moment(d.date, "MMMM YYYY").valueOf() <= currentDate;

                            if (valid) {
                                mobileTooltipCount += d.count;
                            }

                            return valid;
                        })
                        .filter(d => d.state === selectedState)
                        .transition().duration(150)
                        .attr("r", d => radius(d.count) * 1.15);

                    circle // exit
                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                        .filter(d => d.state === selectedState)
                        .filter(d => {
                            // const valid = d.date <= previousDate && d.date > date;
                            const valid = moment(d.date, "MMMM YYYY").valueOf() <= previousDate &&
                                moment(d.date, "MMMM YYYY").valueOf() > currentDate;

                            if (valid) {
                                // avoid negative count
                                if (mobileTooltipCount - d.count < 0) {
                                    mobileTooltipCount = 0;
                                } else {
                                    mobileTooltipCount -= d.count;
                                }
                            }

                            return valid;
                        })
                        .filter(d => d.state === selectedState)
                        .transition().duration(150)
                        .attr("r", 0);

                    // Mobile tooltip
                    mobileTooltipHandler(selectedState, mobileTooltipCount);

                } else {
                    // DEFAULT BEHAVIOR
                    circle // enter
                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                        .filter((d, e) => {
                            // const valid = d.date > previousDate && d.date <= currentDate;
                            const valid = moment(d.date, "MMMM YYYY").valueOf() > previousDate &&
                                moment(d.date, "MMMM YYYY").valueOf() <= currentDate;
                            return valid;
                        })
                        .transition().duration(150)
                        .attr("r", d => radius(d.count) * 1.15);

                    circle // exit
                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                        .filter(d => {
                            // const valid = d.date <= previousDate && d.date > date;
                            const valid = moment(d.date, "MMMM YYYY").valueOf() <= previousDate &&
                                moment(d.date, "MMMM YYYY").valueOf() > currentDate;
                            return valid;
                        })
                        .transition().duration(150)
                        .attr("r", 0);
                }

                previousDate = currentDate;
            },
            resetStateSelector() {
                // Show the circle points for the selected state
                circle
                    .transition().duration(150)
                    .attr("style", "opacity: 0.75");
            }
        });
};

jQuery(document).ready(function () {
    renderNationalTotalFacilitiesTrend();
});
